<template>
  <div>
    <v-select
            v-bind="selectAttrs"
            :items="labels"
            :value="vModel"
            :label="vModel && vModel.length ? '' : inputLabel"
            outlined
            :item-text="itemText"
            :item-value="itemValue"
            multiple
            @change="selectLabels"
    >
      <!-- Overwrite template to always show 'label' inside the select, instead of the selected items -->
      <template v-slot:selection="{ index }">
        <span v-if="!index">{{ inputLabel }}</span>
      </template>
      <!-- Overwrite template to add icons in the list -->
      <template v-slot:item="{ item, attrs, on }">
        <v-list-item v-on="on" v-bind="attrs" #default="{ active }">
          <v-list-item-action :class="{'mr-6': showListIcons}">
          <v-checkbox :input-value="active"></v-checkbox>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title class="d-flex align-center">
              <category-icon
                v-if="showListIcons && !item.icon"
                :iconColor="active ? $color.coro['primary-500'] : 'rgba(0, 0, 0, 0.87)'"
                :categoryKey="item.key"
                class="inline-block mr-2"
                :size="18"
                :width="18"
                :height="18"
              >
              </category-icon>
              <smart-icon
                v-if="showListIcons && item.icon"
                class="label-picker__list-icon"
                :icon="item.icon"
                :icon-color="item.iconColor"
                :size="18"
                :width="18"
                :height="18"
              ></smart-icon>
              <span>{{ item.name }}</span>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-select>
    <div>
      <div v-if="vModel.length" class="d-flex flex-wrap">
        <a
                v-for="selectedLabel in vModel"
                :key="selectedLabel.key"
                v-on:click="() => handleClickLabel(selectedLabel)"
        >
          <slot :selectedLabel="selectedLabel">
            <icon-label
                  :item-text="selectedLabel[itemText]"
                  :item-value="selectedLabel[itemValue]"
                  :icon="itemIcon || selectedLabel.key"
                  :background-color-class="selectedLabel.labelBackgroundColorClass ? selectedLabel.labelBackgroundColorClass : labelBackgroundColorClass"
                  :text-color-class="selectedLabel.labelTextColorClass ? selectedLabel.labelTextColorClass : labelTextColorClass"
                  :icon-color="selectedLabel.iconColor ? selectedLabel.iconColor : iconColor"
                  :closable="removeLabelOnClick"
            ></icon-label>
          </slot>
        </a>
      </div>
      <div v-else>
        <slot
                name="allLabels"
                v-if="allLabelsString"
                :allLabelsString="allLabelsString"
        >
          <icon-label
                  :item-text="allLabelsString"
                  :background-color-class="labelBackgroundColorClass"
                  :text-color-class="labelTextColorClass"
                  :icon-color="$color.coro.blue"
                  style="cursor: default"
          ></icon-label>
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
import IconLabel from "./IconLabel";
import { coro } from "../../../../configuration/colors";
import SmartIcon from './SmartIcon.vue';
import CategoryIcon from './CategoryIcon.vue';

/**
 * Component LabelPicker.vue
 *
 * @module LabelPicker
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.11] app/src/APP_DASHBOARD/templates/components/Parts/LabelPicker.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: "LabelPicker",
  components: {IconLabel, SmartIcon, CategoryIcon},
  methods: {
    selectLabels: function (itemValues) {
      const selectedLabels = []

      for (let i = 0; i < itemValues.length; i++)
        for (let j = 0; j < this.labels.length; j++)
          if (this.labels[j][this.itemValue] === itemValues[i])
            selectedLabels.push(this.labels[j])

      this.vModel = selectedLabels
    },

    handleClickLabel: function(selectedLabel){
        if (this.removeLabelOnClick)
          for (let i = 0; i < this.vModel.length; i++) {
            if (this.vModel[i][this.itemValue] === selectedLabel[this.itemValue]) {
              this.vModel.splice(i, 1)
              break
            }
          }

      this.$emit('click:label', selectedLabel)
    }
  },
  watch : {
    vModel : function(next, prev){
      const arrPrev = prev.slice(0).map(prev => prev[this.itemValue])
      const arrNext = next.slice(0).map(next => next[this.itemValue])

      for (let i=0; i<arrPrev.length;i++){
        if (!arrNext.includes(arrPrev[i]))
          this.$emit('input:itemRemove', prev[i]);
      }

      for (let i=0; i<arrNext.length;i++){
        if (!arrPrev.includes(arrNext[i]))
          this.$emit('input:itemAdd', next[i]);
      }

      this.$emit('input', next);
    },
    value : function(next){
      this.vModel = next;
    },
  },
  data () {
    return {
      vModel: this.value
    }
  },
  computed: {
    vModelItemValues : function(){
      return this.vModel.map((item) => item[this.itemValue])
    }
  },
  props: {
    removeLabelOnClick : {
      type: Boolean,
      default: () => true
    },
    allLabelsString : {
      type: String,
      default: () => 'All Labels'
    },
    iconColor : {
      type: String,
      default : () => coro.blue,
    },
    labelBackgroundColorClass : {
      type: String,
      default : () => 'bg-color-coro-lightblue',
    },
    labelTextColorClass : {
      type: String,
      default : () => 'color-coro-blue',
    },
    itemText : {
      type: String,
      default: () => 'text'
    },
    itemValue : {
      type: String,
      default: () => 'key'
    },
    itemIcon : {
      type: String,
    },
    labels : {
      type: Array,
      default: () => []
    },
    value : {
      type: Array,
      default: () => []
    },
    selectAttrs : {
      type: Object,
      default: () => {}
    },
    inputLabel : {
      type: String,
      default: () => 'Select Labels'
    },
    showListIcons : {
      type: Boolean,
      default: () => false
    }
  }
};
</script>

<style lang="scss">

.label-picker__list-icon {
  max-width: 20px;
  margin-right: 8px;
}
// mobile first

// mobile screens

// tablet screens

// desktop/large screens

// -------------------------------------------------- BEM modifiers ------------------------------------------------
</style>